import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Archive,
  ArrowLeft,
  BellRing,
  Copy,
  Download,
  Eye,
  FileText,
  History,
  LogOut,
  Megaphone,
  Plus,
  Printer,
  Radio,
  Satellite,
  Save,
  Send,
  Trash2,
  Users,
  X,
} from "lucide-react";
import { toast, Toaster } from "sonner";
import {
  Area,
  AreaChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { api, API_BASE } from "@/lib/api";
import { tickerStyle } from "@/lib/ticker";
import { clearToken, getSession } from "@/lib/auth";

const INCIDENT_TYPES = [
  { value: "KEBAKARAN", label: "Kebakaran / Hotspot" },
  { value: "BANJIR", label: "Banjir" },
  { value: "PUTING_BELIUNG", label: "Angin Puting Beliung" },
  { value: "LONGSOR", label: "Tanah Longsor" },
  { value: "ROB", label: "Banjir Rob" },
];

const DISTRICTS = [
  "Sukadana",
  "Simpang Hilir",
  "Teluk Batang",
  "Seponti",
  "Pulau Maya",
  "Kepulauan Karimata",
];

const SEVERITIES = ["AMAN", "WASPADA", "TINGGI"];

const TABS = [
  { id: "thresholds", label: "Ambang Risiko", icon: Radio },
  { id: "reports", label: "Laporan Kejadian", icon: FileText },
  { id: "contacts", label: "Kontak & Notifikasi", icon: Users },
  { id: "archive", label: "Arsip Harian", icon: Archive },
  { id: "rekap", label: "Sebaran Hotspot", icon: Printer },
  { id: "ticker", label: "Info Berjalan", icon: Megaphone },
  { id: "satelit", label: "Satelit", icon: Satellite },
  { id: "visitors", label: "Pengunjung", icon: Eye },
];

export default function AdminPanel() {
  const nav = useNavigate();
  const session = getSession();
  useEffect(() => {
    if (!session) nav("/login");
  }, [session, nav]);

  const [tab, setTab] = useState("thresholds");

  return (
    <main className="admin-shell">
      <Toaster position="top-right" richColors />
      <header className="admin-topbar">
        <Link to="/" data-testid="admin-back">
          <ArrowLeft size={16} /> Dashboard publik
        </Link>
        <Link to="/cetak" className="admin-print-link" data-testid="print-dashboard-link">
          <Printer size={14} /> Cetak Dashboard (PDF)
        </Link>
        <div className="admin-brand">
          <b title="Sistem Informasi Terpadu Antisipasi Kebencanaan Online">SITAKO · PANEL BPBD</b>
          <span>{session?.name}</span>
        </div>
        <button
          className="admin-logout"
          data-testid="logout-button"
          onClick={() => {
            clearToken();
            nav("/login");
          }}
        >
          <LogOut size={14} /> Keluar
        </button>
      </header>

      <div className="admin-tabs" data-testid="admin-tabs">
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              className={tab === t.id ? "active" : ""}
              onClick={() => setTab(t.id)}
              data-testid={`tab-${t.id}`}
            >
              <Icon size={13} /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "thresholds" && <ThresholdEditor />}
      {tab === "reports" && <ReportsManager />}
      {tab === "contacts" && <ContactsAndNotifications />}
      {tab === "archive" && <ArchivePanel />}
      {tab === "rekap" && <RekapPanel />}
      {tab === "ticker" && <TickerPanel />}
      {tab === "satelit" && <SatellitePanel />}
      {tab === "visitors" && <VisitorsPanel />}
    </main>
  );
}

/* ---------- Rekap sebaran hotspot ---------- */
function RekapPanel() {
  const today = new Date().toISOString().slice(0, 10);
  const [mode, setMode] = useState("tanggal");
  const [value, setValue] = useState(today);
  const [hour, setHour] = useState(String(new Date().getHours()).padStart(2, "0"));
  const [start, setStart] = useState(today);
  const [end, setEnd] = useState(today);
  const [rekap, setRekap] = useState(null);
  const [loading, setLoading] = useState(false);

  const buildParams = () => {
    const p = { mode };
    if (mode === "jam") p.value = `${value}T${hour}`;
    else if (mode === "rentang") {
      p.start = start;
      p.end = end;
    } else p.value = value;
    return p;
  };

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/hotspots/rekap", { params: buildParams() });
      setRekap(r.data);
    } catch (e) {
      toast.error("Gagal memuat rekap");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [mode]); // eslint-disable-line

  const changeMode = (m) => {
    setMode(m);
    if (m === "bulan") setValue(today.slice(0, 7));
    else if (m === "tahun") setValue(today.slice(0, 4));
    else setValue(today);
  };

  const pdfUrl = () => {
    const p = new URLSearchParams(buildParams()).toString();
    return `${API_BASE}/hotspots/rekap/pdf?${p}`;
  };

  return (
    <section className="admin-card" data-testid="rekap-panel">
      <div className="admin-card-head">
        <div>
          <span className="section-kicker">SEBARAN TITIK HOTSPOT</span>
          <h2>Kabupaten Kayong Utara</h2>
          <p>
            Rekap per kecamatan &amp; desa berdasarkan tingkat kepercayaan.
            Data terekam otomatis setiap sinkronisasi (SiPongi KLHK + NASA
            FIRMS).
          </p>
        </div>
      </div>

      <div className="rekap-controls" data-testid="rekap-controls">
        <select
          value={mode}
          onChange={(e) => changeMode(e.target.value)}
          data-testid="rekap-mode"
        >
          <option value="jam">Per Jam</option>
          <option value="tanggal">Per Tanggal</option>
          <option value="bulan">Per Bulan</option>
          <option value="tahun">Per Tahun</option>
          <option value="rentang">Rentang Tanggal</option>
        </select>
        {mode === "jam" && (
          <>
            <input type="date" value={value} onChange={(e) => setValue(e.target.value)} />
            <select value={hour} onChange={(e) => setHour(e.target.value)}>
              {Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0")).map((h) => (
                <option key={h} value={h}>
                  {h}.00 WIB
                </option>
              ))}
            </select>
          </>
        )}
        {mode === "tanggal" && (
          <input type="date" value={value} onChange={(e) => setValue(e.target.value)} data-testid="rekap-date" />
        )}
        {mode === "bulan" && (
          <input type="month" value={value} onChange={(e) => setValue(e.target.value)} />
        )}
        {mode === "tahun" && (
          <input
            type="number"
            min="2020"
            max="2100"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            style={{ width: 90 }}
          />
        )}
        {mode === "rentang" && (
          <>
            <input type="date" value={start} onChange={(e) => setStart(e.target.value)} />
            <span className="rekap-sep">s.d.</span>
            <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} />
          </>
        )}
        <button className="admin-primary" onClick={load} disabled={loading} data-testid="rekap-load">
          {loading ? "Memuat…" : "Tampilkan"}
        </button>
        <a
          className="rekap-print"
          href={pdfUrl()}
          target="_blank"
          rel="noreferrer"
          data-testid="rekap-print"
        >
          <Printer size={13} /> Cetak PDF
        </a>
      </div>

      {rekap && (
        <>
          <p className="rekap-label" data-testid="rekap-label">
            {rekap.label} · {rekap.grand.total} titik
          </p>

          <table className="rekap-table" data-testid="rekap-table-kecamatan">
            <thead>
              <tr>
                <th rowSpan={2}>Kecamatan</th>
                <th colSpan={3}>Tingkat Kepercayaan</th>
                <th rowSpan={2}>Total</th>
              </tr>
              <tr>
                <th className="th-rendah">Rendah</th>
                <th className="th-sedang">Sedang</th>
                <th className="th-tinggi">Tinggi</th>
              </tr>
            </thead>
            <tbody>
              {rekap.kecamatan.map((k) => (
                <tr key={k.name}>
                  <td className="td-name">{k.name}</td>
                  <td>{k.rendah}</td>
                  <td>{k.sedang}</td>
                  <td>{k.tinggi}</td>
                  <td className="td-total">{k.total}</td>
                </tr>
              ))}
              <tr className="row-total">
                <td>Total</td>
                <td className="th-rendah">{rekap.grand.rendah}</td>
                <td className="th-sedang">{rekap.grand.sedang}</td>
                <td className="th-tinggi">{rekap.grand.tinggi}</td>
                <td className="td-total">{rekap.grand.total}</td>
              </tr>
            </tbody>
          </table>

          <h3 className="rekap-subtitle">Rekap per Desa</h3>
          <table className="rekap-table rekap-desa" data-testid="rekap-table-desa">
            <thead>
              <tr>
                <th>Kecamatan</th>
                <th>Desa</th>
                <th className="th-rendah">Rendah</th>
                <th className="th-sedang">Sedang</th>
                <th className="th-tinggi">Tinggi</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              {rekap.kecamatan.flatMap((k) =>
                k.desa.map((d, i) => (
                  <tr key={`${k.name}-${d.desa}`} className={d.total > 0 ? "row-hit" : ""}>
                    <td className="td-kec">{i === 0 ? k.name : ""}</td>
                    <td className="td-name">{d.desa}</td>
                    <td>{d.rendah}</td>
                    <td>{d.sedang}</td>
                    <td>{d.tinggi}</td>
                    <td className="td-total">{d.total}</td>
                  </tr>
                )),
              )}
            </tbody>
          </table>
        </>
      )}
    </section>
  );
}

/* ---------- Visitors tab ---------- */
function SatellitePanel() {
  const nav = useNavigate();
  const SATS = [
    { id: "NOAA20", name: "NASA-NOAA20", desc: "VIIRS di satelit NOAA-20 (JPSS-1) · resolusi 375 m · lintasan ±01.30 & 13.30 WIB" },
    { id: "SNPP", name: "NASA-SNPP", desc: "VIIRS di satelit Suomi-NPP · resolusi 375 m · lintasan ±01.00 & 13.00 WIB" },
    { id: "MODIS", name: "NASA-MODIS", desc: "MODIS di satelit Terra & Aqua · resolusi 1 km · 4 lintasan per hari" },
  ];
  const [selected, setSelected] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .get("/settings")
      .then((r) => setSelected(r.data.hotspot_satellites || ["NOAA20", "SNPP", "MODIS"]))
      .catch(() => {});
  }, []);

  const toggle = (id) =>
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const save = async () => {
    if (!selected.length) {
      toast.error("Pilih minimal satu satelit");
      return;
    }
    setSaving(true);
    try {
      await api.put("/settings", { hotspot_satellites: selected });
      toast.success("Sumber satelit tersimpan — data peta akan dimuat ulang");
    } catch (e) {
      if (e?.response?.status === 401) {
        toast.error("Sesi login berakhir — silakan login ulang");
        nav("/login");
      } else if (e?.response?.status === 422 || e?.response?.status === 404) {
        toast.error(
          "Versi server belum mendukung pengaturan satelit — lakukan deploy ulang aplikasi",
        );
      } else {
        const detail = e?.response?.data?.detail;
        toast.error(
          typeof detail === "string"
            ? `Gagal menyimpan: ${detail}`
            : "Gagal menyimpan pilihan satelit — periksa koneksi lalu coba lagi",
        );
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="admin-card" data-testid="satellite-panel">
      <div className="panel-heading">
        <div>
          <span className="section-kicker">SUMBER SATELIT TITIK PANAS</span>
          <h3>Pilih satelit yang dipakai (SiPongi & NASA FIRMS)</h3>
        </div>
      </div>
      <p className="visitor-note">
        Pilihan berlaku untuk peta, metrik, tren, dan rekap. Perubahan langsung
        diterapkan (cache dibersihkan otomatis). Minimal satu satelit harus aktif.
      </p>
      <div className="sat-list">
        {SATS.map((s) => (
          <label
            key={s.id}
            className={`sat-item ${selected.includes(s.id) ? "on" : ""}`}
            data-testid={`sat-toggle-${s.id.toLowerCase()}`}
          >
            <input
              type="checkbox"
              checked={selected.includes(s.id)}
              onChange={() => toggle(s.id)}
            />
            <div>
              <b>{s.name}</b>
              <span>{s.desc}</span>
            </div>
          </label>
        ))}
      </div>
      <button
        className="admin-primary"
        onClick={save}
        disabled={saving}
        data-testid="satellite-save"
      >
        <Save size={14} /> Simpan
      </button>
    </section>
  );
}

function TickerPanel() {
  const nav = useNavigate();
  const [text, setText] = useState("");
  const [enabled, setEnabled] = useState(false);
  const [speed, setSpeed] = useState("sedang");
  const [size, setSize] = useState("sedang");
  const [waNumber, setWaNumber] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .get("/settings")
      .then((r) => {
        setText(r.data.ticker_text || "");
        setEnabled(Boolean(r.data.ticker_enabled));
        setSpeed(r.data.ticker_speed || "sedang");
        setSize(r.data.ticker_size || "sedang");
        setWaNumber(r.data.whatsapp_number || "");
      })
      .catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ticker_text: text,
        ticker_enabled: enabled,
        ticker_speed: speed,
        ticker_size: size,
      };
      if (waNumber) payload.whatsapp_number = waNumber;
      await api.put("/settings", payload);
      toast.success("Informasi berjalan tersimpan");
    } catch (e) {
      if (e?.response?.status === 401) {
        toast.error("Sesi login berakhir — silakan login ulang");
        nav("/login");
      } else {
        const detail = e?.response?.data?.detail;
        toast.error(
          typeof detail === "string"
            ? `Gagal menyimpan: ${detail}`
            : "Gagal menyimpan informasi berjalan — coba muat ulang halaman",
        );
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="admin-card" data-testid="ticker-panel">
      <div className="panel-heading">
        <div>
          <span className="section-kicker">INFORMASI BERJALAN</span>
          <h3>Teks berjalan di atas dashboard publik</h3>
        </div>
      </div>
      <p className="visitor-note">
        Teks ini tampil sebagai informasi berjalan (running text) berwarna mencolok di
        bagian paling atas dashboard publik, lengkap dengan jam digital. Kosongkan atau
        nonaktifkan untuk menyembunyikan.
      </p>
      <textarea
        className="ticker-input"
        rows={3}
        maxLength={500}
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Contoh: SIAGA KARHUTLA — Dilarang membakar lahan. Laporkan titik api ke BPBD Kayong Utara 0812-5609-2999"
        data-testid="ticker-text-input"
      />
      <div className="ticker-actions">
        <label className="select-wrap">
          <span>Kecepatan</span>
          <select
            value={speed}
            onChange={(e) => setSpeed(e.target.value)}
            data-testid="ticker-speed-select"
          >
            <option value="super_lambat">Super lambat</option>
            <option value="lambat">Lambat</option>
            <option value="sedang">Sedang</option>
            <option value="cepat">Cepat</option>
          </select>
        </label>
        <label className="select-wrap">
          <span>Ukuran huruf</span>
          <select
            value={size}
            onChange={(e) => setSize(e.target.value)}
            data-testid="ticker-size-select"
          >
            <option value="kecil">Kecil</option>
            <option value="sedang">Sedang</option>
            <option value="besar">Besar</option>
          </select>
        </label>
        <label className="ticker-toggle" data-testid="ticker-enabled-toggle">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
          />
          Tampilkan di dashboard
        </label>
        <button
          className="admin-primary"
          onClick={save}
          disabled={saving}
          data-testid="ticker-save"
        >
          <Save size={14} /> Simpan
        </button>
      </div>
      {text && (
        <div className="ticker-preview" data-testid="ticker-preview">
          <span className="tp-label">PRATINJAU</span>
          <div className="ticker-bar tb-preview">
            <span className="ticker-badge">INFO</span>
            <div className="ticker-scroll">
              <p style={tickerStyle(speed, size)}>
                {text} &nbsp;&nbsp;•&nbsp;&nbsp; {text}
              </p>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}


function VisitorsPanel() {
  const [summary, setSummary] = useState(null);
  const [daily, setDaily] = useState([]);

  useEffect(() => {
    api.get("/visitors/summary").then((r) => setSummary(r.data));
    api
      .get("/visitors/daily?days=30")
      .then((r) => setDaily((r.data.items || []).slice().reverse()))
      .catch(() => toast.error("Gagal memuat statistik pengunjung"));
  }, []);

  return (
    <section className="admin-card" data-testid="visitors-panel">
      <div className="admin-card-head">
        <div>
          <span className="section-kicker">STATISTIK PENGUNJUNG</span>
          <h3>Akses dashboard SITAKO</h3>
        </div>
        <Eye size={18} />
      </div>

      <div className="visitor-stats">
        <div className="vs-chip" data-testid="visitors-total">
          <span>Total kunjungan</span>
          <b>{summary ? summary.total.toLocaleString("id-ID") : "…"}</b>
        </div>
        <div className="vs-chip" data-testid="visitors-today">
          <span>Hari ini (WIB)</span>
          <b>{summary ? summary.today.toLocaleString("id-ID") : "…"}</b>
        </div>
      </div>

      <div className="chart" data-testid="visitors-chart">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={daily}>
            <defs>
              <linearGradient id="visGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#38bdf8" stopOpacity={0.5} />
                <stop offset="100%" stopColor="#38bdf8" stopOpacity={0} />
              </linearGradient>
            </defs>
            <XAxis
              dataKey="date"
              tick={{ fill: "#718178", fontSize: 10 }}
              tickFormatter={(v) => v.slice(5)}
            />
            <YAxis
              tick={{ fill: "#718178", fontSize: 10 }}
              width={36}
              allowDecimals={false}
            />
            <Tooltip
              contentStyle={{
                background: "#0f2440",
                border: "1px solid #2a5488",
                color: "#f1f5f9",
                fontSize: 12,
              }}
              formatter={(value) => [`${value} kunjungan`, "Pengunjung"]}
            />
            <Area
              type="monotone"
              dataKey="count"
              stroke="#38bdf8"
              strokeWidth={2}
              fill="url(#visGrad)"
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
      <p className="visitor-note">
        Kunjungan dihitung sekali per sesi browser · agregat harian tanpa data
        pribadi pengunjung.
      </p>
    </section>
  );
}

/* ---------- Thresholds tab (with history) ---------- */
function ThresholdEditor() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState([]);

  const loadHistory = () =>
    api.get("/thresholds/history").then((r) => setHistory(r.data.items || []));

  useEffect(() => {
    api.get("/thresholds").then((r) => setForm(r.data));
    loadHistory();
  }, []);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        rain_watch: parseFloat(form.rain_watch),
        rain_high: parseFloat(form.rain_high),
        wave_watch: parseFloat(form.wave_watch),
        wave_high: parseFloat(form.wave_high),
        hotspot_watch: parseInt(form.hotspot_watch, 10),
        hotspot_high: parseInt(form.hotspot_high, 10),
      };
      const r = await api.put("/thresholds", payload);
      setForm(r.data);
      await loadHistory();
      toast.success("Ambang risiko tersimpan · perubahan tercatat");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan");
    } finally {
      setSaving(false);
    }
  };

  if (!form)
    return <div className="admin-loading">Memuat ambang risiko...</div>;

  const fields = [
    { key: "rain_watch", label: "Hujan · Waspada (mm)", step: 0.1 },
    { key: "rain_high", label: "Hujan · Tinggi (mm)", step: 0.1 },
    { key: "wave_watch", label: "Gelombang · Waspada (m)", step: 0.05 },
    { key: "wave_high", label: "Gelombang · Tinggi (m)", step: 0.05 },
    { key: "hotspot_watch", label: "Hotspot · Waspada (titik)", step: 1 },
    { key: "hotspot_high", label: "Hotspot · Tinggi (titik)", step: 1 },
  ];

  return (
    <>
      <section className="admin-card" data-testid="threshold-editor">
        <div className="admin-card-head">
          <div>
            <span className="section-kicker">AMBANG RISIKO</span>
            <h2>Kalibrasi indikator peringatan</h2>
            <p>
              Nilai yang dilewati memicu status <b>WASPADA</b> lalu{" "}
              <b>TINGGI</b> pada peta dan alert publik.
            </p>
          </div>
          {form.updated_by && (
            <div className="admin-meta">
              <span>Terakhir diubah</span>
              <b>{form.updated_by}</b>
              <small>{new Date(form.updated_at).toLocaleString("id-ID")}</small>
            </div>
          )}
        </div>
        <div className="admin-fields">
          {fields.map((f) => (
            <label key={f.key}>
              <span>{f.label}</span>
              <input
                type="number"
                step={f.step}
                value={form[f.key]}
                onChange={(e) => update(f.key, e.target.value)}
                data-testid={`threshold-${f.key}`}
              />
            </label>
          ))}
        </div>
        <button
          className="admin-primary"
          onClick={save}
          disabled={saving}
          data-testid="save-thresholds"
        >
          <Save size={15} /> {saving ? "Menyimpan..." : "Simpan ambang risiko"}
        </button>
      </section>

      <section className="admin-card" data-testid="threshold-history">
        <div className="admin-card-head">
          <div>
            <span className="section-kicker">RIWAYAT AMBANG</span>
            <h2>
              <History size={18} style={{ verticalAlign: "-3px" }} /> Jejak
              keputusan
            </h2>
            <p>Log siapa mengubah ambang, kapan, dan nilai sebelum-sesudah.</p>
          </div>
        </div>
        {history.length === 0 ? (
          <div className="report-empty">
            Belum ada perubahan tercatat. Ubah salah satu nilai di atas untuk
            memulai riwayat.
          </div>
        ) : (
          <div className="history-list">
            {history.map((h) => (
              <div className="history-row" key={h.id}>
                <div className="hr-head">
                  <b>{h.changed_by}</b>
                  <small>{new Date(h.changed_at).toLocaleString("id-ID")}</small>
                </div>
                <div className="hr-diffs">
                  {Object.keys(h.after).map((k) =>
                    h.before?.[k] !== h.after?.[k] ? (
                      <div className="hr-diff" key={k}>
                        <span>{k}</span>
                        <b>
                          <s>{String(h.before?.[k] ?? "—")}</s> →{" "}
                          <em>{String(h.after?.[k])}</em>
                        </b>
                      </div>
                    ) : null,
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </>
  );
}

/* ---------- Reports tab ---------- */
function ReportsManager() {
  const [items, setItems] = useState([]);
  const [creating, setCreating] = useState(false);
  const [filterDistrict, setFilterDistrict] = useState("Semua wilayah");
  const emptyForm = {
    title: "",
    incident_type: "BANJIR",
    district: "Sukadana",
    location_detail: "",
    lat: "",
    lng: "",
    severity: "WASPADA",
    description: "",
    actions_taken: "",
    reporter: "",
  };
  const [form, setForm] = useState(emptyForm);

  const load = async () => {
    const r = await api.get("/reports", {
      params:
        filterDistrict !== "Semua wilayah" ? { district: filterDistrict } : {},
    });
    setItems(r.data.items || []);
  };
  useEffect(() => {
    load();
  }, [filterDistrict]); // eslint-disable-line

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form };
      if (payload.lat === "") delete payload.lat;
      else payload.lat = parseFloat(payload.lat);
      if (payload.lng === "") delete payload.lng;
      else payload.lng = parseFloat(payload.lng);
      Object.keys(payload).forEach(
        (k) => payload[k] === "" && delete payload[k],
      );
      await api.post("/reports", payload);
      setForm(emptyForm);
      setCreating(false);
      await load();
      toast.success("Laporan diterbitkan");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan laporan");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Hapus laporan ini?")) return;
    await api.delete(`/reports/${id}`);
    await load();
    toast.success("Laporan dihapus");
  };

  const share = (slug) => {
    const url = `${window.location.origin}/laporan/${slug}`;
    navigator.clipboard.writeText(url);
    toast.success("Tautan disalin");
  };

  const pdfUrl = (id) => `${API_BASE}/reports/${id}/pdf`;

  return (
    <section className="admin-card" data-testid="reports-manager">
      <div className="admin-card-head">
        <div>
          <span className="section-kicker">LAPORAN KEJADIAN</span>
          <h2>Terbitkan & bagikan laporan resmi</h2>
          <p>
            Setiap laporan dapat diunduh PDF atau dibagikan via tautan digital
            publik.
          </p>
        </div>
        <div className="admin-toolbar">
          <select
            value={filterDistrict}
            onChange={(e) => setFilterDistrict(e.target.value)}
            data-testid="reports-filter"
          >
            <option>Semua wilayah</option>
            {DISTRICTS.map((d) => (
              <option key={d}>{d}</option>
            ))}
          </select>
          <button
            className="admin-primary"
            onClick={() => setCreating((v) => !v)}
            data-testid="toggle-create-report"
          >
            <Plus size={15} /> {creating ? "Tutup" : "Laporan baru"}
          </button>
        </div>
      </div>

      {creating && (
        <form
          className="report-form"
          onSubmit={submit}
          data-testid="report-form"
        >
          <div className="rf-row">
            <label>
              <span>Judul</span>
              <input
                required
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                data-testid="rf-title"
              />
            </label>
            <label>
              <span>Jenis</span>
              <select
                value={form.incident_type}
                onChange={(e) =>
                  setForm({ ...form, incident_type: e.target.value })
                }
                data-testid="rf-type"
              >
                {INCIDENT_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div className="rf-row">
            <label>
              <span>Kecamatan</span>
              <select
                value={form.district}
                onChange={(e) => setForm({ ...form, district: e.target.value })}
                data-testid="rf-district"
              >
                {DISTRICTS.map((d) => (
                  <option key={d}>{d}</option>
                ))}
              </select>
            </label>
            <label>
              <span>Tingkat</span>
              <select
                value={form.severity}
                onChange={(e) => setForm({ ...form, severity: e.target.value })}
                data-testid="rf-severity"
              >
                {SEVERITIES.map((s) => (
                  <option key={s}>{s}</option>
                ))}
              </select>
            </label>
            <label>
              <span>Pelapor</span>
              <input
                value={form.reporter}
                onChange={(e) => setForm({ ...form, reporter: e.target.value })}
                placeholder="Nama · Instansi"
              />
            </label>
          </div>
          <div className="rf-row">
            <label>
              <span>Lokasi detail</span>
              <input
                value={form.location_detail}
                onChange={(e) =>
                  setForm({ ...form, location_detail: e.target.value })
                }
                placeholder="Desa / dusun / jalan"
              />
            </label>
            <label>
              <span>Latitude</span>
              <input
                type="number"
                step="0.0001"
                value={form.lat}
                onChange={(e) => setForm({ ...form, lat: e.target.value })}
              />
            </label>
            <label>
              <span>Longitude</span>
              <input
                type="number"
                step="0.0001"
                value={form.lng}
                onChange={(e) => setForm({ ...form, lng: e.target.value })}
              />
            </label>
          </div>
          <label>
            <span>Uraian kejadian</span>
            <textarea
              rows={3}
              required
              value={form.description}
              onChange={(e) =>
                setForm({ ...form, description: e.target.value })
              }
              data-testid="rf-description"
            />
          </label>
          <label>
            <span>Tindakan yang dilakukan</span>
            <textarea
              rows={2}
              value={form.actions_taken}
              onChange={(e) =>
                setForm({ ...form, actions_taken: e.target.value })
              }
            />
          </label>
          <button
            type="submit"
            className="admin-primary"
            data-testid="rf-submit"
          >
            <FileText size={15} /> Terbitkan laporan
          </button>
        </form>
      )}

      <div className="report-list" data-testid="report-list">
        {items.length === 0 && (
          <div className="report-empty">
            Belum ada laporan pada wilayah ini.
          </div>
        )}
        {items.map((r) => (
          <article
            className="report-item"
            key={r.id}
            data-testid={`report-${r.public_slug}`}
          >
            <div className="ri-head">
              <div>
                <span className={`sev sev-${r.severity.toLowerCase()}`}>
                  {r.severity}
                </span>
                <b>{r.title}</b>
                <small>
                  #{r.public_slug.toUpperCase()} ·{" "}
                  {r.incident_type.replace("_", " ")} · {r.district}
                </small>
              </div>
              <div className="ri-actions">
                <button
                  onClick={() => share(r.public_slug)}
                  title="Salin tautan"
                >
                  <Copy size={14} /> Tautan
                </button>
                <a href={pdfUrl(r.id)} target="_blank" rel="noreferrer">
                  <Download size={14} /> PDF
                </a>
                <button className="danger" onClick={() => remove(r.id)}>
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
            <p>{r.description}</p>
            <footer>
              <span>Oleh {r.reporter || r.created_by || "—"}</span>
              <span>{new Date(r.created_at).toLocaleString("id-ID")}</span>
            </footer>
          </article>
        ))}
      </div>
    </section>
  );
}

/* ---------- Contacts + notifications tab ---------- */
function ContactsAndNotifications() {
  const [contacts, setContacts] = useState([]);
  const [notifs, setNotifs] = useState([]);
  const [filter, setFilter] = useState("pending");

  const loadAll = async () => {
    const [c, n] = await Promise.all([
      api.get("/contacts"),
      api.get("/notifications", { params: { status: filter } }),
    ]);
    setContacts(c.data.items || []);
    setNotifs(n.data.items || []);
  };
  useEffect(() => {
    loadAll();
    const t = setInterval(loadAll, 45000);
    return () => clearInterval(t);
  }, [filter]); // eslint-disable-line

  return (
    <>
      <WaSettingCard />

      <section className="admin-card" data-testid="contacts-panel">
        <div className="admin-card-head">
          <div>
            <span className="section-kicker">KONTAK CAMAT</span>
            <h2>Daftar penerima peringatan</h2>
            <p>Isi nomor WhatsApp/telepon camat setiap kecamatan supaya notifikasi TINGGI otomatis diarahkan ke penerima yang tepat.</p>
          </div>
        </div>
        <div className="contact-grid">
          {contacts.map((c) => (
            <ContactRow key={c.district} contact={c} onSaved={loadAll} />
          ))}
        </div>
      </section>

      <section className="admin-card" data-testid="notifications-panel">
        <div className="admin-card-head">
          <div>
            <span className="section-kicker">ANTREAN NOTIFIKASI</span>
            <h2>
              <BellRing size={18} style={{ verticalAlign: "-3px" }} /> Peringatan tersimpan
            </h2>
            <p>Status TINGGI otomatis ter-queue di sini (throttle 3 jam per kombinasi wilayah × jenis).</p>
          </div>
          <div className="admin-toolbar">
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              data-testid="notif-filter"
            >
              <option value="pending">Menunggu</option>
              <option value="sent">Terkirim</option>
              <option value="dismissed">Diabaikan</option>
              <option value="all">Semua</option>
            </select>
          </div>
        </div>
        <NotificationList items={notifs} onChanged={loadAll} />
      </section>
    </>
  );
}

/* ---------- Pengaturan WA layanan informasi ---------- */
function WaSettingCard() {
  const [number, setNumber] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => setNumber(r.data.whatsapp_number || ""));
  }, []);

  const save = async () => {
    if (!number.trim()) {
      toast.error("Nomor tidak boleh kosong");
      return;
    }
    setSaving(true);
    try {
      const r = await api.put("/settings", { whatsapp_number: number.trim() });
      setNumber(r.data.whatsapp_number);
      toast.success("Nomor layanan informasi tersimpan");
    } catch (e) {
      toast.error("Gagal menyimpan nomor");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="admin-card" data-testid="wa-setting-card">
      <div className="admin-card-head">
        <div>
          <span className="section-kicker">LAYANAN INFORMASI PUBLIK</span>
          <h2>Nomor WhatsApp footer</h2>
          <p>
            Nomor ini tampil di bagian bawah dashboard publik dan menjadi
            tujuan tombol chat WhatsApp pengunjung.
          </p>
        </div>
      </div>
      <div className="wa-setting-row">
        <input
          value={number}
          onChange={(e) => setNumber(e.target.value)}
          placeholder="cth: 0812-5609-2999"
          data-testid="wa-setting-input"
        />
        <button
          className="admin-primary"
          onClick={save}
          disabled={saving}
          data-testid="wa-setting-save"
        >
          <Save size={13} /> {saving ? "Menyimpan…" : "Simpan"}
        </button>
      </div>
    </section>
  );
}

function ContactRow({ contact, onSaved }) {
  const [form, setForm] = useState({
    camat_name: contact.camat_name || "",
    phone: contact.phone || "",
    whatsapp: contact.whatsapp || "",
    notes: contact.notes || "",
  });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/contacts/${encodeURIComponent(contact.district)}`, form);
      toast.success(`Kontak ${contact.district} tersimpan`);
      await onSaved();
    } catch (e) {
      toast.error("Gagal menyimpan");
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="contact-card" data-testid={`contact-${contact.district.toLowerCase().replaceAll(" ", "-")}`}>
      <header>
        <b>{contact.district}</b>
        {contact.updated_at && (
          <small>{new Date(contact.updated_at).toLocaleDateString("id-ID")}</small>
        )}
      </header>
      <label>
        <span>Nama camat</span>
        <input
          value={form.camat_name}
          onChange={(e) => setForm({ ...form, camat_name: e.target.value })}
          placeholder="Nama lengkap"
        />
      </label>
      <div className="c-row">
        <label>
          <span>WhatsApp</span>
          <input
            value={form.whatsapp}
            onChange={(e) => setForm({ ...form, whatsapp: e.target.value })}
            placeholder="628…"
          />
        </label>
        <label>
          <span>Telepon</span>
          <input
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            placeholder="0534…"
          />
        </label>
      </div>
      <label>
        <span>Catatan</span>
        <input
          value={form.notes}
          onChange={(e) => setForm({ ...form, notes: e.target.value })}
          placeholder="Kanal alternatif / catatan"
        />
      </label>
      <button
        onClick={save}
        disabled={saving}
        className="contact-save"
        data-testid={`save-contact-${contact.district.toLowerCase().replaceAll(" ", "-")}`}
      >
        <Save size={13} /> {saving ? "Menyimpan..." : "Simpan"}
      </button>
    </div>
  );
}

function NotificationList({ items, onChanged }) {
  if (!items.length)
    return (
      <div className="report-empty">
        Tidak ada notifikasi pada filter ini.
      </div>
    );
  const markSent = async (id) => {
    await api.post(`/notifications/${id}/sent`);
    toast.success("Ditandai terkirim");
    onChanged();
  };
  const dismiss = async (id) => {
    await api.post(`/notifications/${id}/dismiss`);
    onChanged();
  };
  return (
    <div className="notif-list" data-testid="notif-list">
      {items.map((n) => (
        <article className={`notif-item lv-${n.level.toLowerCase()}`} key={n.id}>
          <div className="ni-head">
            <span className={`sev sev-${n.level.toLowerCase()}`}>{n.level}</span>
            <b>{n.district}</b>
            <small>{n.alert_type.replace("_", " ")}</small>
            <span className={`status status-${n.status}`}>{n.status.toUpperCase()}</span>
          </div>
          <p>{n.message}</p>
          {n.targets?.length ? (
            <div className="ni-targets">
              {n.targets.map((t, idx) => (
                <span key={idx}>
                  <b>{t.name}</b> · {t.whatsapp || t.phone || "—"}
                </span>
              ))}
            </div>
          ) : (
            <div className="ni-targets none">Belum ada kontak camat untuk wilayah ini</div>
          )}
          <footer>
            <span>{new Date(n.created_at).toLocaleString("id-ID")}</span>
            {n.status === "pending" && (
              <div className="ni-actions">
                <button onClick={() => markSent(n.id)}>
                  <Send size={13} /> Tandai terkirim
                </button>
                <button className="danger" onClick={() => dismiss(n.id)}>
                  <X size={13} /> Abaikan
                </button>
              </div>
            )}
          </footer>
        </article>
      ))}
    </div>
  );
}

/* ---------- Archive tab ---------- */
function ArchivePanel() {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = () =>
    api.get("/archive").then((r) => setItems(r.data.items || []));
  useEffect(() => {
    load();
  }, []);

  const snapshot = async () => {
    setBusy(true);
    try {
      await api.post("/archive/snapshot");
      await load();
      toast.success("Snapshot hari ini disimpan");
    } catch (e) {
      toast.error("Gagal membuat snapshot");
    } finally {
      setBusy(false);
    }
  };

  const chartData = useMemo(
    () =>
      [...items]
        .sort((a, b) => a.date.localeCompare(b.date))
        .map((s) => ({
          date: s.date.slice(5),
          rain: s.avg_rain,
          wave: s.avg_wave,
          high: s.high_risk_count,
        })),
    [items],
  );

  return (
    <section className="admin-card" data-testid="archive-panel">
      <div className="admin-card-head">
        <div>
          <span className="section-kicker">ARSIP HARIAN</span>
          <h2>Riwayat 30 hari terakhir</h2>
          <p>
            Snapshot otomatis tersimpan setiap hari dan dipangkas setelah 30
            hari untuk menjaga performa. Anda juga bisa memaksa snapshot manual
            di bawah.
          </p>
        </div>
        <button
          className="admin-primary"
          onClick={snapshot}
          disabled={busy}
          data-testid="force-snapshot"
        >
          <Archive size={15} /> {busy ? "Menyimpan..." : "Snapshot sekarang"}
        </button>
      </div>

      {chartData.length > 0 && (
        <div className="archive-chart">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData}>
              <defs>
                <linearGradient id="archRain" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#0ea5a5" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#0ea5a5" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="date" tick={{ fill: "#718178", fontSize: 10 }} />
              <YAxis tick={{ fill: "#718178", fontSize: 10 }} width={28} />
              <Tooltip
                contentStyle={{
                  background: "#15231b",
                  border: "1px solid #2a3d30",
                  color: "#ecfdf5",
                }}
              />
              <Area
                type="monotone"
                dataKey="rain"
                stroke="#0ea5a5"
                fill="url(#archRain)"
                strokeWidth={2}
              />
              <Area
                type="monotone"
                dataKey="wave"
                stroke="#e5a83b"
                fill="none"
                strokeWidth={2}
              />
              <Area
                type="monotone"
                dataKey="high"
                stroke="#ef6b6b"
                fill="none"
                strokeWidth={1.5}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      {items.length === 0 ? (
        <div className="report-empty">Belum ada snapshot tersimpan.</div>
      ) : (
        <table className="archive-table" data-testid="archive-table">
          <thead>
            <tr>
              <th>Tanggal</th>
              <th>Sumber</th>
              <th>Hotspot</th>
              <th>Rata-rata hujan</th>
              <th>Rata-rata gelombang</th>
              <th>Risiko tinggi</th>
            </tr>
          </thead>
          <tbody>
            {items.map((s) => (
              <tr key={s.id}>
                <td>
                  <b>{s.date}</b>
                </td>
                <td>
                  <small>{s.source}</small>
                </td>
                <td>{s.hotspot_total} titik</td>
                <td>{s.avg_rain} mm</td>
                <td>{s.avg_wave} m</td>
                <td>
                  <span
                    className={`sev sev-${
                      s.high_risk_count > 0 ? "tinggi" : "aman"
                    }`}
                  >
                    {s.high_risk_count} wilayah
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
